import path from "path";
import { useCallback, useEffect, useMemo, useState } from "react";
import { shallowEqual, useSelector } from "react-redux";
import { updateJumpList } from "../../../extensions/starter_dashlet/util";
import * as fs from "../../../util/fs";
import { activeGameId } from "../../../util/selectors";
import { truthy } from "../../../util/util";
import { generateGameStarter, generateToolStarters } from "./toolStarters";
const MAX_PINNED_TOOLS = 5;
async function validateTools(starters, discoveryPath) {
    const validIds = [];
    for (const starter of starters) {
        if (!starter?.exePath)
            continue;
        const exePath = path.isAbsolute(starter.exePath)
            ? starter.exePath
            : path.join(discoveryPath, starter.exePath);
        try {
            await fs.statAsync(exePath);
            validIds.push(starter.id);
        }
        catch {
            // tool exe not found - not valid
        }
    }
    return validIds;
}
export const useToolsData = () => {
    const gameMode = useSelector(activeGameId);
    const knownGames = useSelector((state) => state.session.gameMode.known);
    const discoveredGames = useSelector((state) => state.settings.gameMode.discovered);
    const discoveredTools = useSelector((state) => state.settings.gameMode.discovered?.[gameMode]?.tools ?? {}, shallowEqual);
    const toolsOrder = useSelector((state) => state.settings.interface.tools?.order?.[gameMode] ?? [], shallowEqual);
    const primaryTool = useSelector((state) => state.settings.interface.primaryTool?.[gameMode]);
    const pinnedToolsMap = useSelector((state) => state.settings.interface.tools?.pinned?.[gameMode] ?? {}, shallowEqual);
    const toolsRunning = useSelector((state) => state.session.base.toolsRunning);
    const mods = useSelector((state) => state.persistent.mods?.[gameMode]);
    const deploymentCounter = useSelector((state) => state.persistent?.deployment?.deploymentCounter?.[gameMode] ?? 0);
    // ── Tool generation & validation ────────────────────────────────────────
    const [validToolIds, setValidToolIds] = useState([]);
    const tools = useMemo(() => {
        const gameStarter = generateGameStarter(knownGames, discoveredGames, gameMode);
        const newTools = generateToolStarters(knownGames, discoveredGames, discoveredTools, toolsOrder, gameMode, gameStarter?.id);
        const jumpList = truthy(gameStarter)
            ? [gameStarter].concat(newTools)
            : newTools;
        updateJumpList(jumpList);
        return newTools;
    }, [knownGames, discoveredGames, discoveredTools, toolsOrder, gameMode]);
    useEffect(() => {
        const discoveryPath = discoveredGames[gameMode]?.path;
        if (discoveryPath !== undefined) {
            void validateTools(tools, discoveryPath).then(setValidToolIds);
        }
    }, [tools, discoveredGames, gameMode, mods, deploymentCounter]);
    // ── Derived: hidden / pinned / unpinned / launcher split ─────────────────
    const isToolHidden = useCallback((starter) => discoveredTools[starter.id]?.hidden === true, [discoveredTools]);
    const isToolPinned = useCallback((starter) => pinnedToolsMap[starter.id] === true, [pinnedToolsMap]);
    const { launcherTool, otherPinnedTools, unpinnedTools } = useMemo(() => {
        // Hidden tools (removed in classic UI) are excluded entirely
        const visible = tools.filter((s) => s.isGame || discoveredTools[s.id] === undefined || !isToolHidden(s));
        const launcher = visible.find((s) => s.id === primaryTool);
        const nonLauncher = visible.filter((s) => s.id !== primaryTool);
        // Tools with explicit pinned=true go to pinned section.
        // Tools with no pinned state (undefined) go to unpinned (default).
        // Tools with explicit pinned=false go to unpinned.
        const pinned = nonLauncher.filter((s) => isToolPinned(s));
        const unpinned = nonLauncher.filter((s) => !isToolPinned(s));
        return {
            launcherTool: launcher,
            otherPinnedTools: pinned,
            unpinnedTools: unpinned,
        };
    }, [
        tools,
        primaryTool,
        discoveredTools,
        pinnedToolsMap,
        isToolHidden,
        isToolPinned,
    ]);
    const pinnedCount = otherPinnedTools.length;
    const maxPinnedReached = pinnedCount >= MAX_PINNED_TOOLS;
    const isToolValid = useCallback((starter) => validToolIds.includes(starter.id), [validToolIds]);
    const isToolRunning = useCallback((starter) => {
        const starterInfo = starter;
        return (starterInfo?.exePath !== undefined &&
            toolsRunning[starterInfo.exePath] !== undefined);
    }, [toolsRunning]);
    return {
        gameMode,
        tools,
        toolsOrder,
        knownGames,
        discoveredGames,
        discoveredTools,
        primaryTool,
        launcherTool,
        otherPinnedTools,
        unpinnedTools,
        pinnedCount,
        maxPinnedReached,
        MAX_PINNED_TOOLS,
        isToolValid,
        isToolRunning,
        isToolHidden,
    };
};
